import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Box, Flex, Text } from '@chakra-ui/react';

interface LoadingAnimationProps {
  isExiting?: boolean;
}

const ACCENT_RGB = 'var(--chakra-colors-brand-primary-rgb)';

const LoadingAnimation = memo(({ isExiting = false }: LoadingAnimationProps) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: isExiting ? 0 : 1,
        y: isExiting ? -20 : 0,
        scale: isExiting ? 0.9 : 1,
      }}
      exit={{
        opacity: 0,
        y: -20,
        scale: 0.9
      }}
      transition={{
        duration: 0.5,
        ease: "easeInOut"
      }}
      style={{
        position: 'absolute',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '150px'
      }}
    >
      <Box position="relative" width="300px" height="150px">
        <Flex
          position="relative"
          width="full"
          height="full"
          justify="center"
          align="center"
          direction="column"
        >
          {/* Main animation container */}
          <motion.div
            animate={{
              rotateY: isExiting ? 90 : 0,
            }}
            transition={{
              duration: 0.5,
              ease: "easeInOut"
            }}
          >
            <Box position="relative" width="260px" height="80px">
              {/* Left icon (GPX) */}
              <Flex
                position="absolute"
                left="0"
                top="0"
                width="60px"
                height="60px"
                bg="bg.surface"
                borderRadius="md"
                justify="center"
                align="center"
                border="1px solid"
                borderColor="border.subtle"
                boxShadow="sm"
              >
                <motion.div
                  animate={{
                    scale: isExiting ? [1, 0.8, 0] : [1, 1.05, 1],
                    rotate: isExiting ? [0, -10, 0] : [0, -3, 0, 3, 0]
                  }}
                  transition={{
                    duration: isExiting ? 0.4 : 3,
                    repeat: isExiting ? 0 : Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    color="brand.primary"
                  >
                    GPX
                  </Text>
                </motion.div>
              </Flex>

              {/* Right icon (KML) */}
              <Flex
                position="absolute"
                right="0"
                top="0"
                width="60px"
                height="60px"
                bg="bg.surface"
                borderRadius="md"
                justify="center"
                align="center"
                border="1px solid"
                borderColor="border.subtle"
                boxShadow="sm"
              >
                <motion.div
                  animate={{
                    scale: isExiting ? [1, 0.8, 0] : [1, 1.05, 1],
                    rotate: isExiting ? [0, 10, 0] : [0, 3, 0, -3, 0]
                  }}
                  transition={{
                    duration: isExiting ? 0.4 : 3,
                    repeat: isExiting ? 0 : Infinity,
                    ease: "easeInOut",
                    delay: isExiting ? 0 : 1.5
                  }}
                >
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    color="brand.secondary"
                  >
                    KML
                  </Text>
                </motion.div>
              </Flex>

              {/* Map route line */}
              <motion.div
                animate={{
                  opacity: isExiting ? 0 : 1,
                  width: isExiting ? "0px" : "135px",
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeInOut"
                }}
              >
                <Box
                  position="absolute"
                  height="3px"
                  left="62px"
                  top="30px"
                  bg="border.subtle"
                  borderRadius="full"
                  width="135px"
                />
              </motion.div>

              {/* Flowing dots between the icons */}
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: i === 1 ? '14px' : '12px',
                    height: i === 1 ? '14px' : '12px',
                    borderRadius: '50%',
                    backgroundColor: i === 1
                      ? 'var(--chakra-colors-brand-primary)'
                      : `rgba(${ACCENT_RGB}, ${0.7 - i * 0.15})`,
                    boxShadow: i === 1 ? `0 0 10px rgba(${ACCENT_RGB}, 0.6)` : 'none',
                    left: '62px',
                    top: '30px',
                  }}
                  animate={
                    isExiting
                      ? {
                        opacity: 0,
                        x: (i - 1.5) * 25,
                        y: i % 2 === 0 ? -25 : 25,
                        scale: 0
                      }
                      : {
                        left: ['62px', '197px'],
                        top: [
                          '30px',
                          '15px',
                          '30px',
                          '45px',
                          '30px'
                        ],
                        scale: i === 1 ? [1, 1.2, 1] : [0.8, 1, 0.8],
                      }
                  }
                  transition={
                    isExiting
                      ? {
                        duration: 0.3 + i * 0.1,
                        ease: "easeOut"
                      }
                      : {
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.4,
                        ease: "easeInOut",
                        times: [0, 0.25, 0.5, 0.75, 1]
                      }
                  }
                />
              ))}
            </Box>
          </motion.div>

          {/* Loading text */}
          <Flex mt={8} alignItems="center">
            <motion.div
              animate={{
                y: isExiting ? 10 : 0,
                opacity: isExiting ? 0 : 1,
              }}
              transition={{
                duration: 0.3,
                ease: "easeOut"
              }}
            >
              <Text
                fontWeight="semibold"
                fontSize="md"
                color="text.primary"
                mr={2}
              >
                Converting
              </Text>
            </motion.div>
            <Flex alignItems="center">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={
                    isExiting
                      ? {
                        opacity: 0,
                        x: (i - 1) * 10,
                      }
                      : {
                        opacity: [0.3, 1, 0.3],
                        y: [0, -3, 0]
                      }
                  }
                  transition={
                    isExiting
                      ? {
                        duration: 0.2 + i * 0.1,
                        ease: "easeOut"
                      }
                      : {
                        duration: 1.2,
                        repeat: Infinity,
                        delay: i * 0.2,
                        ease: "easeInOut"
                      }
                  }
                  style={{ marginLeft: '3px' }}
                >
                  <Box
                    as="span"
                    display="inline-block"
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="brand.primary"
                  />
                </motion.div>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </motion.div>
  );
});

export default LoadingAnimation;
